// Home's to-do list (BRIEF.md section 1): what needs the owner, nothing else. Empty means done.
import { useState } from 'react';
import { useOrgData } from './accountant/useOrgData.js';
import type { Api } from './api.js';
import { EmptyState } from './EmptyState.js';
import { day, dollars, fromCents } from './format.js';

export type Item =
  | { kind: 'setup_bank' }
  | {
      kind: 'receipt_gst';
      receiptId: string;
      supplier: string;
      date: string;
      receiptGstMinor: string;
      recordedGstMinor: string;
    }
  | {
      kind: 'missing_receipts';
      lines: { date: string; description: string; amountMinor: string }[];
    }
  | { kind: 'sort'; bankAccountId: string; name: string; count: number }
  | { kind: 'bas_ready'; periodStart: string; label: string }
  | { kind: 'stripe_failed'; connectionId: string; name: string; message: string }
  | { kind: 'stripe_difference'; connectionId: string; name: string; differenceMinor: string }
  | {
      kind: 'stripe_flag';
      id: string;
      name: string;
      date: string;
      amountMinor: string;
      description: string | null;
      flag: string;
      gst: boolean;
    };

/** How many things wait in each area, for the sidebar counts. */
export function waiting(items: Item[]): { money: number; bills: number; tax: number } {
  let money = 0;
  let bills = 0;
  let tax = 0;
  for (const item of items) {
    if (item.kind === 'sort') money += item.count;
    else if (item.kind === 'bas_ready') tax += 1;
    else if (item.kind === 'missing_receipts') bills += item.lines.length;
    else if (item.kind === 'receipt_gst') bills += 1;
    else money += 1;
  }
  return { money, bills, tax };
}

export function Todo({
  api,
  orgId,
  onOpenMoney,
  onOpenTax,
}: {
  api: Api;
  orgId: string;
  onOpenMoney: () => void;
  onOpenTax?: () => void;
}) {
  const todo = useOrgData<{ items: Item[] }>(api, orgId, '/todo');
  if (todo.error) return <p className="error">{todo.error}</p>;
  if (!todo.data) return <p className="muted">Loading</p>;
  const items = todo.data.items;
  if (items.length === 0)
    return (
      <EmptyState art="all-sorted">
        <h2>All done</h2>
        <p className="muted">Nothing needs you right now. Your books are up to date.</p>
      </EmptyState>
    );
  return (
    <section className="todo-list">
      <h2>To do</h2>
      {items.map((item) =>
        item.kind === 'stripe_flag' ? (
          <StripeFlag key={item.id} api={api} orgId={orgId} item={item} onDone={todo.reload} />
        ) : item.kind === 'receipt_gst' ? (
          <ReceiptGst
            key={item.receiptId}
            api={api}
            orgId={orgId}
            item={item}
            onDone={todo.reload}
          />
        ) : item.kind === 'setup_bank' ? (
          <div key="setup-bank" className="todo">
            <p>
              Add your bank account and upload your first statement: Foderis sorts most of it for
              you.
            </p>
            <button type="button" onClick={onOpenMoney}>
              Add your bank account
            </button>
          </div>
        ) : item.kind === 'bas_ready' ? (
          <div key={`bas-${item.periodStart}`} className="todo">
            <p>
              Your BAS for {item.label} is ready to copy into the ATO, or to record if your agent
              lodged it.
            </p>
            {onOpenTax && (
              <button type="button" onClick={onOpenTax}>
                Open BAS
              </button>
            )}
          </div>
        ) : item.kind === 'missing_receipts' ? (
          <MissingReceipts key="missing" item={item} />
        ) : (
          <div
            key={`${item.kind}-${'bankAccountId' in item ? item.bankAccountId : item.connectionId}`}
            className="todo"
          >
            <p className={item.kind === 'sort' ? undefined : 'error'}>{describe(item)}</p>
            <button type="button" onClick={onOpenMoney}>
              {item.kind === 'sort' ? 'Sort now' : 'Open Money'}
            </button>
          </div>
        ),
      )}
    </section>
  );
}

function describe(
  item: Exclude<
    Item,
    { kind: 'stripe_flag' | 'receipt_gst' | 'missing_receipts' | 'setup_bank' | 'bas_ready' }
  >,
): string {
  switch (item.kind) {
    case 'sort':
      return `${item.count} ${item.count === 1 ? 'transaction' : 'transactions'} to sort in ${item.name}.`;
    case 'stripe_failed':
      return `Stripe ${item.name}: ${item.message}`;
    case 'stripe_difference':
      return `Stripe ${item.name}: your books and Stripe differ by ${dollars(fromCents(item.differenceMinor))}.`;
  }
}

function StripeFlag(props: {
  api: Api;
  orgId: string;
  item: Extract<Item, { kind: 'stripe_flag' }>;
  onDone: () => void;
}) {
  const { api, orgId, item } = props;
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function review(choice: 'kept' | 'gst' | 'export') {
    setBusy(true);
    setError('');
    try {
      await api.send(orgId, 'POST', `/stripe/transactions/${item.id}/review`, { review: choice });
      props.onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your answer.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="todo stack">
      <p>
        <strong>
          {item.description ?? `Stripe ${item.name}`}, {dollars(fromCents(item.amountMinor))} on{' '}
          {day(item.date)}
        </strong>
        <br />
        {item.flag} It is recorded {item.gst ? 'with GST included' : 'as an overseas sale, no GST'}.
      </p>
      <div className="title-row">
        <button type="button" disabled={busy} onClick={() => review('kept')}>
          Keep it {item.gst ? 'with GST' : 'without GST'}
        </button>
        <button
          type="button"
          className="link"
          disabled={busy}
          onClick={() => review(item.gst ? 'export' : 'gst')}
        >
          {item.gst
            ? 'It is an overseas customer: no GST'
            : 'It is an Australian customer: include GST'}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function ReceiptGst(props: {
  api: Api;
  orgId: string;
  item: Extract<Item, { kind: 'receipt_gst' }>;
  onDone: () => void;
}) {
  const { api, orgId, item } = props;
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const money = (minor: string) => dollars(fromCents(minor));
  async function apply() {
    setBusy(true);
    setError('');
    try {
      await api.send(orgId, 'POST', `/receipts/${item.receiptId}/apply-gst`);
      props.onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the GST.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="todo stack">
      <p>
        The {item.supplier} receipt from {day(item.date)} shows{' '}
        {item.receiptGstMinor === '0' ? 'no GST' : `GST of ${money(item.receiptGstMinor)}`}, but
        your books claim {item.recordedGstMinor === '0' ? 'none' : money(item.recordedGstMinor)}.
      </p>
      <button type="button" disabled={busy} onClick={apply}>
        Use the receipt's GST
      </button>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function MissingReceipts({ item }: { item: Extract<Item, { kind: 'missing_receipts' }> }) {
  return (
    <div className="todo stack">
      <p>
        {item.lines.length === 1
          ? 'One purchase over $82.50 claims GST without a receipt.'
          : `${item.lines.length} purchases over $82.50 claim GST without a receipt.`}{' '}
        The ATO needs a tax invoice to claim that GST: forward or upload the receipts on the Bills
        tab.
      </p>
      <table className="table">
        <tbody>
          {item.lines.map((l) => (
            <tr key={`${l.date}-${l.description}-${l.amountMinor}`}>
              <td>{day(l.date)}</td>
              <td>{l.description}</td>
              <td className="num">{dollars(fromCents(l.amountMinor))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
